(() => {
  if (customElements.get('campaign-selection')) return;

  const normalizeMode = (mode) => mode === 'quantity' ? 'quantity' : 'amount';
  const normalizeTiers = (tiers, mode = 'amount') => {
    const tierMode = normalizeMode(mode);
    const byThreshold = new Map();
    (tiers || []).forEach(({ threshold, discount }) => {
      if (!Number.isSafeInteger(threshold) || threshold <= 0 || !Number.isSafeInteger(discount) || discount < 0) return;
      byThreshold.set(threshold, {
        threshold,
        discount: tierMode === 'amount' ? Math.min(discount, threshold) : Math.min(discount, 10000)
      });
    });
    return [...byThreshold.values()].sort((a, b) => a.threshold - b.threshold);
  };
  const calculate = (items, tiers, mode = 'amount') => {
    let subtotal = 0, original = 0, count = 0;
    items.forEach((item) => {
      count += item.quantity;
      subtotal += item.price * item.quantity;
      original += Math.max(item.price, item.compare) * item.quantity;
    });
    const tierMode = normalizeMode(mode);
    const metric = tierMode === 'quantity' ? count : subtotal;
    const current = tiers.filter((tier) => metric >= tier.threshold).at(-1);
    const next = tiers.find((tier) => metric < tier.threshold);
    const tierDiscount = current?.discount || 0;
    const discount = Math.min(subtotal, tierMode === 'quantity' ? Math.round(subtotal * tierDiscount / 10000) : tierDiscount);
    const total = subtotal - discount;
    return { subtotal, original, count, metric, current, total, discount, saving: original - total, next, progress: tiers.length ? Math.min(100, metric / tiers.at(-1).threshold * 100) : 0 };
  };
  const formatMoney = (cents, sample) => {
    const pattern = /1([,.\s'’]?)234[,.\s'’]?567([,.]89)?|1([,.\s'’]?)234[,.\s'’]?568/;
    const match = String(sample).match(pattern);
    if (!match) return (cents / 100).toFixed(2);
    const separator = match[1] ?? match[3] ?? '';
    const decimals = match[2] ? 2 : 0;
    const parts = (cents / 100).toFixed(decimals).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    return String(sample).replace(match[0], parts.join(match[2]?.[0] || '.'));
  };
  const formatPercent = (basisPoints) => `${Number((basisPoints / 100).toFixed(2))}%`;
  window.iGardenSelectionMath = Object.freeze({ normalizeTiers, calculate, formatMoney, formatPercent });
  const sharedControllers = window.iGardenSelectionGroups instanceof Map ? window.iGardenSelectionGroups : new Map();
  window.iGardenSelectionGroups = sharedControllers;

  class CampaignSelection extends HTMLElement {
    connectedCallback() {
      if (this.relocating || this.controller) return;
      const configNode = this.querySelector('[data-selection-config]');
      if (!configNode) return;
      this.config = JSON.parse(configNode.textContent);
      this.tierMode = normalizeMode(this.config.tierMode);
      this.tiers = normalizeTiers(this.config.tiers, this.tierMode);
      this.selectionRole = this.dataset.selectionRole || 'standalone';
      this.selectionGroup = String(this.dataset.selectionGroup || '').trim();
      this.isSharedHost = this.selectionRole === 'shared_host' && Boolean(this.selectionGroup);
      this.section = document.getElementById(`shopify-section-${this.dataset.sectionId}`);
      if (!this.section) return;
      this.hostTarget = this.section.querySelector('[data-selection-enabled="true"]');
      if (!this.hostTarget) return;
      if (this.isSharedHost) {
        const existingController = sharedControllers.get(this.selectionGroup);
        if (existingController?.isConnected && existingController !== this) {
          this.hidden = true;
          console.warn(`Only one shared campaign selection host is allowed for group "${this.selectionGroup}".`);
          return;
        }
        sharedControllers.set(this.selectionGroup, this);
      }
      this.items = new Map();
      this.cardButtonLabels = new WeakMap();
      this.cards = [];
      this.targets = [];
      this.anchorTargets = [];
      this.sections = [];
      this.placeholders = new Map();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.dialog = this.querySelector('.campaign-selection__dialog');
      this.confirmDialog = this.querySelector('.campaign-selection__confirm');
      this.panel = this.querySelector('.campaign-selection__panel');
      // A transformed/clipped section can become the containing block for fixed
      // descendants. Keep a spacer here and render the complete UI under body,
      // preserving its inline theme variables, dialogs and delegated events.
      this.placeholder = this.createPlaceholder();
      this.before(this.placeholder);
      this.placeholders.set(this.hostTarget, this.placeholder);
      this.relocating = true;
      try {
        document.body.append(this);
      } finally {
        this.relocating = false;
      }
      this.addEventListener('click', (event) => this.onClick(event), options);
      this.addEventListener('input', (event) => {
        if (!event.target.matches('[data-selection-input]')) return;
        const quantity = Number(event.target.value);
        if (event.target.value.trim() !== '' && Number.isSafeInteger(quantity) && quantity > 0) this.setQuantity(event.target.closest('[data-id]')?.dataset.id, quantity);
      }, options);
      this.addEventListener('change', (event) => {
        if (!event.target.matches('[data-selection-input]')) return;
        const id = event.target.closest('[data-id]')?.dataset.id;
        const value = event.target.value.trim();
        if (value !== '') this.setQuantity(id, Number(value));
        this.update();
      }, options);
      this.addEventListener('focusout', (event) => {
        if (!event.target.matches('[data-selection-input]')) return;
        const item = this.items.get(event.target.closest('[data-id]')?.dataset.id);
        if (item) event.target.value = item.quantity;
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      }, options);
      this.dialog.addEventListener('close', () => this.restoreScroll(), options);
      this.confirmDialog.addEventListener('cancel', (event) => { event.preventDefault(); this.resolveConfirmation(false); }, options);
      this.querySelector('[data-selection-thumbnails]').addEventListener('scroll', () => this.updateScroll(), { ...options, passive: true });
      window.addEventListener('resize', () => this.scheduleLayout(), options);
      window.addEventListener('scroll', () => this.schedulePosition(), { ...options, passive: true });
      window.addEventListener('campaign-tabs:change', () => this.scheduleGroupRefresh(), options);
      document.addEventListener('shopify:block:select', (event) => {
        if (event.target === this || this.contains(event.target) || this.targets.some((target) => target.contains(event.target))) {
          this.activePlaceholder()?.scrollIntoView({ block: 'end' });
        }
      }, options);
      document.addEventListener('shopify:section:load', () => this.scheduleGroupRefresh(), options);
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target === this.section) this.remove();
        else if (this.sections.includes(event.target)) this.scheduleGroupRefresh();
      }, options);
      this.sectionObserver = new MutationObserver(() => {
        if (!this.section.isConnected) this.remove();
      });
      this.sectionObserver.observe(document.body, { childList: true, subtree: true });
      this.observer = new ResizeObserver(() => this.scheduleLayout());
      this.observer.observe(this.panel);
      this.panel.inert = true;
      this.visibilityObserver = new IntersectionObserver(() => this.updateVisibility());
      this.refreshGroup();
      this.buildMarkers();
      this.ready = true;
      this.update();
    }

    disconnectedCallback() {
      if (this.relocating) return;
      this.resolveConfirmation(false);
      this.controller?.abort();
      this.observer?.disconnect();
      this.visibilityObserver?.disconnect();
      this.sectionObserver?.disconnect();
      this.classList.remove('is-visible', 'is-docked');
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.positionFrame);
      cancelAnimationFrame(this.groupFrame);
      window.clearTimeout(this.tabRefreshTimer);
      this.frame = null;
      this.positionFrame = null;
      this.groupFrame = null;
      this.sections?.forEach((section) => {
        if (section.selectionController === this) delete section.selectionController;
      });
      this.placeholders?.forEach((placeholder) => placeholder.remove());
      this.placeholders?.clear();
      this.placeholder = null;
      this.restoreScroll();
      if (this.isSharedHost && sharedControllers.get(this.selectionGroup) === this) sharedControllers.delete(this.selectionGroup);
      this.controller = null;
    }

    createPlaceholder() {
      const placeholder = document.createElement('div');
      placeholder.className = 'campaign-selection-placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      return placeholder;
    }

    sectionForTarget(target) {
      return target?.closest('[id^="shopify-section-"]') || null;
    }

    captureCardLabels() {
      this.cards.forEach((card) => {
        card.querySelectorAll('[data-selection-add], .fathers_day-grid-card__add').forEach((button) => {
          if (this.cardButtonLabels.has(button)) return;
          this.cardButtonLabels.set(button, button.querySelector('svg')
            ? button.getAttribute('aria-label') || 'Add to Cart'
            : button.textContent.trim());
        });
      });
    }

    refreshGroup() {
      const previousSections = this.sections || [];
      const targets = this.isSharedHost
        ? [...document.querySelectorAll('[data-selection-enabled="true"][data-selection-group]')].filter((target) => {
          const role = target.dataset.selectionRole;
          return target.dataset.selectionGroup === this.selectionGroup && (role === 'shared_host' || role === 'shared_member');
        })
        : [this.hostTarget];

      this.targets = [...new Set(targets.filter((target) => target?.isConnected))];
      this.anchorTargets = this.targets.filter((target) => target.dataset.selectionAnchor === 'true');
      if (!this.anchorTargets.length && this.hostTarget?.isConnected) this.anchorTargets = [this.hostTarget];
      this.sections = [...new Set(this.targets.map((target) => this.sectionForTarget(target)).filter(Boolean))];
      previousSections.forEach((section) => {
        if (!this.sections.includes(section) && section.selectionController === this) delete section.selectionController;
      });
      this.sections.forEach((section) => { section.selectionController = this; });

      this.placeholders.forEach((placeholder, target) => {
        if (!this.anchorTargets.includes(target)) {
          placeholder.remove();
          this.placeholders.delete(target);
        }
      });
      this.anchorTargets.forEach((target) => {
        if (this.placeholders.has(target)) return;
        const placeholder = this.createPlaceholder();
        target.append(placeholder);
        this.placeholders.set(target, placeholder);
      });

      this.cards = this.targets.flatMap((target) => [...target.querySelectorAll('[data-selection-card], .fathers_day-grid-card')]);
      this.captureCardLabels();
      this.visibilityObserver?.disconnect();
      this.anchorTargets.forEach((target) => this.visibilityObserver?.observe(target));
      this.updateVisibility();
      if (this.ready) this.update();
    }

    scheduleGroupRefresh() {
      if (this.groupFrame) return;
      this.groupFrame = requestAnimationFrame(() => {
        this.groupFrame = null;
        this.refreshGroup();
        this.scheduleLayout();
        window.clearTimeout(this.tabRefreshTimer);
        this.tabRefreshTimer = window.setTimeout(() => {
          if (!this.isConnected) return;
          this.updateVisibility();
          this.scheduleLayout();
        }, 240);
      });
    }

    targetIsDisplayed(target) {
      if (!target?.isConnected || target.hidden) return false;
      const style = getComputedStyle(target);
      const rect = target.getBoundingClientRect();
      return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0' && rect.width > 0 && rect.height > 0;
    }

    targetIsInViewport(target) {
      if (!this.targetIsDisplayed(target)) return false;
      const rect = target.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
    }

    updateVisibility() {
      const visible = this.anchorTargets.some((target) => this.targetIsInViewport(target));
      this.classList.toggle('is-visible', visible);
      if (this.panel) this.panel.inert = !visible;
      this.schedulePosition();
    }

    activePlaceholder() {
      const activeTarget = this.anchorTargets.find((target) => this.targetIsInViewport(target))
        || this.anchorTargets.find((target) => this.targetIsDisplayed(target))
        || this.hostTarget;
      return this.placeholders.get(activeTarget) || this.placeholder;
    }

    money(value) { return formatMoney(value, this.dataset.moneySample); }
    tierDiscount(value) { return this.tierMode === 'quantity' ? formatPercent(value) : this.money(value); }
    text(template, values) { return String(template || '').replace(/\{(\w+)\}/g, (match, key) => values[key] ?? match); }
    currentData(card) { return card.querySelector('[data-selection-option].is-active, .fathers_day-grid-card__variant.is-active') || card; }
    isAvailable(source) {
      // Campaign stock is a visual schedule; Shopify availability controls selection.
      return source.dataset.selectionAvailable === 'true';
    }

    accessoriesForCard(card) {
      const container = card?.querySelector('[data-accessories]');
      if (!container || container.dataset.accessoriesExcluded === 'true') return [];
      return [...container.querySelectorAll('[data-accessory-variant-id].is-selected')]
        .filter((accessory) => accessory.dataset.accessoryApplicable !== 'false')
        .map((accessory) => ({
          id: String(accessory.dataset.accessoryVariantId || '').trim(),
          title: accessory.dataset.accessoryTitle || accessory.querySelector('.fathers_day-grid-card__accessory-title')?.textContent.trim() || '',
          image: accessory.dataset.accessoryImage || accessory.querySelector('img')?.currentSrc || accessory.querySelector('img')?.src || '',
          free: accessory.dataset.accessoryFree === 'true'
        }))
        .filter((accessory) => accessory.id);
    }

    updateAccessoriesFromCard(card) {
      if (this.busy || !card) return;
      const id = this.currentData(card).dataset.selectionId;
      const item = this.items.get(id);
      if (!item) return;
      item.accessories = this.accessoriesForCard(card);
      this.update();
    }

    addFromCard(card) {
      if (this.busy) return;
      const source = this.currentData(card), data = source.dataset;
      if (!data.selectionId || !this.isAvailable(source) || card.classList.contains('is-campaign-sold-out')) {
        this.showError(this.config.unavailableText);
        return;
      }
      const id = data.selectionId;
      const existing = this.items.get(id);
      const quantity = (existing?.quantity || 0) + 1;
      if (!Number.isSafeInteger(Number(data.selectionPrice)) || Number(data.selectionPrice) < 0 || !Number.isSafeInteger(Number(data.selectionCompare) * quantity)) return;
      this.items.set(id, {
        id,
        price: Number(data.selectionPrice),
        compare: Number(data.selectionCompare),
        title: data.selectionTitle,
        variant: data.selectionVariant,
        image: data.selectionImage || '',
        quantity,
        source,
        discountCodes: String(data.selectionDiscountCode || '')
          .split(',')
          .map((code) => code.trim())
          .filter(Boolean),
        accessories: this.accessoriesForCard(card)
      });
      this.showError('');
      this.update();
      this.querySelector('[data-selection-status]').textContent = this.text(this.config.addedText, { title: data.selectionTitle, count: this.items.get(id).quantity });
    }

    setQuantity(id, quantity) {
      if (this.busy || !Number.isSafeInteger(quantity) || quantity < 0) return;
      const item = this.items.get(id);
      if (!item) return;
      if (quantity > item.quantity && !this.isAvailable(item.source)) { this.showError(this.config.unavailableText); return; }
      if (!Number.isSafeInteger(Math.max(item.price, item.compare) * quantity)) return;
      const activeRow = this.dialog?.contains(document.activeElement) ? document.activeElement.closest('[data-id]') : null;
      const focusAfterRemoval = quantity === 0 && activeRow?.dataset.id === id ? activeRow.nextElementSibling || activeRow.previousElementSibling : null;
      if (quantity === 0) this.items.delete(id);
      else item.quantity = quantity;
      this.update();
      if (quantity === 0 && activeRow?.dataset.id === id) (focusAfterRemoval?.querySelector('button') || this.querySelector('[data-selection-close]')).focus();
    }

    buildMarkers() {
      const markers = this.querySelector('[data-selection-markers]');
      markers.replaceChildren();
      this.querySelector('[data-selection-progress]').hidden = !this.tiers.length;
      if (!this.tiers.length) return;
      [{ threshold: 0, discount: 0 }, ...this.tiers].forEach((tier) => {
        const marker = document.createElement('div');
        marker.className = 'campaign-selection__marker';
        marker.style.setProperty('--marker-position', `${tier.threshold / this.tiers.at(-1).threshold * 100}%`);
        marker.dataset.threshold = tier.threshold;
        const label = document.createElement('span');
        label.textContent = tier.threshold ? `${this.tierDiscount(tier.discount)} ${this.config.offText}` : this.tierDiscount(0);
        const thresholdText = this.tierMode === 'quantity'
          ? `${tier.threshold} ${this.config.quantityUnitText}`
          : this.money(tier.threshold);
        marker.title = `${thresholdText}: ${label.textContent}`;
        marker.append(label);
        markers.append(marker);
      });
    }

    update() {
      const totals = calculate([...this.items.values()], this.tiers, this.tierMode);
      this.totals = totals;
      const nextTemplate = this.tierMode === 'quantity' ? this.config.quantityNextText : this.config.nextText;
      const remaining = totals.next ? Math.max(0, totals.next.threshold - totals.metric) : 0;
      const message = !totals.count ? this.config.emptyText : totals.next
        ? this.text(nextTemplate, {
          amount: this.tierMode === 'amount' ? this.money(remaining) : this.money(0),
          count: this.tierMode === 'quantity' ? remaining : 0,
          discount: this.tierDiscount(totals.next.discount)
        })
        : this.tiers.length ? this.text(this.config.unlockedText, { discount: this.tierDiscount(totals.current?.discount || 0) }) : this.config.emptyText;
      this.querySelector('[data-selection-message]').textContent = message;
      this.querySelector('[data-selection-count]').textContent = `${totals.count} ${this.config.selectedText}`;
      this.querySelector('[data-selection-total]').textContent = this.money(totals.total);
      const original = this.querySelector('[data-selection-original]'), saving = this.querySelector('[data-selection-saving]');
      original.textContent = this.money(totals.original); original.hidden = totals.saving <= 0;
      saving.textContent = `${this.money(totals.saving)} ${this.config.offText}`; saving.hidden = totals.saving <= 0;
      this.querySelector('[data-selection-fill]').style.width = `${totals.progress}%`;
      this.querySelector('[data-selection-progress]').setAttribute('aria-valuenow', String(Math.round(totals.progress)));
      this.querySelectorAll('.campaign-selection__marker').forEach((node) => node.classList.toggle('is-reached', totals.metric >= Number(node.dataset.threshold)));
      const checkout = this.querySelector('[data-selection-checkout]');
      checkout.disabled = !totals.count || this.busy;
      checkout.textContent = this.busy ? this.config.loadingText : this.config.checkoutText;
      this.querySelector('[data-selection-empty]').hidden = this.items.size > 0;
      this.syncItems('[data-selection-thumbnails]', '[data-selection-thumbnail-template]', false);
      this.syncItems('[data-selection-rows]', '[data-selection-row-template]', true);
      this.cards.forEach((card) => {
        const count = this.items.get(this.currentData(card).dataset.selectionId)?.quantity || 0;
        card.querySelectorAll('[data-selection-add], .fathers_day-grid-card__add').forEach((button) => {
          const originalLabel = this.cardButtonLabels.get(button);
          const label = count ? `${originalLabel} (${count})` : originalLabel;
          if (!button.querySelector('svg')) button.textContent = label;
          button.setAttribute('aria-label', label);
        });
      });
      this.scheduleLayout();
    }

    syncItems(containerSelector, templateSelector, detailed) {
      const container = this.querySelector(containerSelector);
      [...container.children].forEach((node) => { if (!this.items.has(node.dataset.id)) node.remove(); });
      this.items.forEach((item) => {
        let node = [...container.children].find((entry) => entry.dataset.id === item.id);
        if (!node) {
          node = this.querySelector(templateSelector).content.firstElementChild.cloneNode(true);
          node.dataset.id = item.id;
          container.append(node);
        }
        const img = node.querySelector('img');
        if (item.image && img.getAttribute('src') !== item.image) img.src = item.image;
        img.alt = item.title; img.hidden = !item.image;
        if (detailed) {
          node.querySelector('[data-selection-variant]').textContent = item.variant;
          node.querySelector('[data-selection-unit]').textContent = this.money(item.price);
          const compare = node.querySelector('[data-selection-compare]');
          compare.textContent = this.money(item.compare); compare.hidden = item.compare <= item.price;
          const input = node.querySelector('input');
          if (input !== document.activeElement) input.value = item.quantity;
          node.querySelector('[data-selection-minus-icon]').hidden = item.quantity === 1;
          node.querySelector('[data-selection-delete-icon]').hidden = item.quantity !== 1;
        } else {
          node.querySelector('[data-selection-quantity]').textContent = item.quantity;
          node.querySelector('[data-selection-quantity]').hidden = item.quantity === 1;
        }
        this.syncAccessories(node.querySelector('[data-selection-accessories]'), item, detailed);
        node.querySelectorAll('button, input').forEach((control) => { control.disabled = Boolean(this.busy); });
      });
    }

    syncAccessories(container, item, detailed) {
      if (!container) return;
      const accessories = Array.isArray(item.accessories) ? item.accessories : [];
      container.replaceChildren();
      container.hidden = accessories.length === 0;
      container.setAttribute('aria-label', accessories.map((accessory) => accessory.title).filter(Boolean).join(', ') || 'Accessories');
      const visibleAccessories = detailed ? accessories : accessories.slice(0, 3);
      visibleAccessories.forEach((accessory) => {
        if (!detailed) {
          const badge = document.createElement('span');
          badge.className = 'campaign-selection__thumbnail-accessory';
          badge.title = accessory.title;
          if (accessory.image) {
            const image = document.createElement('img');
            image.src = accessory.image;
            image.alt = accessory.title;
            image.width = 24;
            image.height = 24;
            image.loading = 'lazy';
            badge.append(image);
          } else {
            badge.textContent = '+';
          }
          container.append(badge);
          return;
        }

        const row = document.createElement('div');
        row.className = 'campaign-selection__row-accessory';
        if (accessory.image) {
          const image = document.createElement('img');
          image.src = accessory.image;
          image.alt = accessory.title;
          image.width = 40;
          image.height = 40;
          image.loading = 'lazy';
          row.append(image);
        }
        const title = document.createElement('span');
        title.className = 'campaign-selection__row-accessory-title';
        title.textContent = accessory.title;
        const meta = document.createElement('span');
        meta.className = 'campaign-selection__row-accessory-meta';
        meta.textContent = `×${item.quantity}${accessory.free ? ' · Free' : ''}`;
        row.append(title, meta);
        container.append(row);
      });
      if (!detailed && accessories.length > visibleAccessories.length) {
        const more = document.createElement('span');
        more.className = 'campaign-selection__thumbnail-accessory campaign-selection__thumbnail-accessory--more';
        more.textContent = `+${accessories.length - visibleAccessories.length}`;
        container.append(more);
      }
    }

    checkoutItems() {
      const quantities = new Map();
      const add = (id, quantity) => {
        const normalizedId = String(id || '').trim();
        if (!normalizedId || !Number.isSafeInteger(quantity) || quantity < 1) return;
        quantities.set(normalizedId, (quantities.get(normalizedId) || 0) + quantity);
      };
      this.items.forEach((item) => {
        add(item.id, item.quantity);
        (item.accessories || []).forEach((accessory) => add(accessory.id, item.quantity));
      });
      return [...quantities].map(([id, quantity]) => ({ id, quantity }));
    }

    onClick(event) {
      const target = event.target.closest('button');
      if (!target) return;
      const id = target.closest('[data-id]')?.dataset.id;
      if (target.matches('[data-selection-remove]')) this.setQuantity(id, 0);
      else if (target.matches('[data-selection-minus]')) this.setQuantity(id, (this.items.get(id)?.quantity || 1) - 1);
      else if (target.matches('[data-selection-plus]')) this.setQuantity(id, (this.items.get(id)?.quantity || 0) + 1);
      else if (target.matches('[data-selection-view]')) {
        this.previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        this.dialog.showModal();
      } else if (target.matches('[data-selection-close]')) this.dialog.close();
      else if (target.matches('[data-selection-scroll]')) this.querySelector('[data-selection-thumbnails]').scrollBy({ left: 200, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      else if (target.matches('[data-selection-checkout]')) this.checkout();
      else if (target.matches('[data-selection-cancel]')) this.resolveConfirmation(false);
      else if (target.matches('[data-selection-continue]')) this.resolveConfirmation(true);
    }

    restoreScroll() {
      if (this.previousOverflow !== undefined) document.documentElement.style.overflow = this.previousOverflow;
      this.previousOverflow = undefined;
    }

    confirm(text) {
      if (!this.isConnected) return Promise.resolve(false);
      this.querySelector('[data-selection-confirm-text]').textContent = text;
      this.confirmDialog.showModal();
      return new Promise((resolve) => { this.confirmResolve = resolve; });
    }

    resolveConfirmation(value) {
      this.confirmDialog?.close();
      this.confirmResolve?.(value);
      this.confirmResolve = null;
    }

    async checkout() {
      const discountCodes = new Set();
      this.items.forEach((item) => {
        const itemCodes = item.discountCodes?.length
          ? item.discountCodes
          : String(item.source?.dataset.selectionDiscountCode || '').split(',');
        itemCodes
          .map((code) => String(code || '').trim())
          .filter(Boolean)
          .forEach((code) => discountCodes.add(code));
      });

      if (this.busy || !this.items.size) return;
      if ([...this.items.values()].some((item) => !this.isAvailable(item.source))) { this.showError(this.config.unavailableText); return; }
      this.busy = true;
      this.showError('');
      this.update();
      try {
        if (!window.iGardenCart?.checkoutSelection) throw new Error('Selection checkout is unavailable.');
        await window.iGardenCart.checkoutSelection(this.checkoutItems(), {
          source: 'campaign-selection',
          currency: this.dataset.selectionCurrency,
          discountCodes: Array.from(discountCodes)
        });
      } catch (error) {
        this.showError(error.restoreFailed ? this.config.restoreErrorText : this.config.errorText);
        console.error('Campaign selection checkout failed', error);
      } finally {
        this.busy = false;
        if (this.isConnected) this.update();
      }
    }

    showError(message) {
      const error = this.querySelector('[data-selection-error]');
      error.textContent = message; error.hidden = !message;
    }

    updateScroll() {
      const strip = this.querySelector('[data-selection-thumbnails]');
      this.querySelector('[data-selection-scroll]').hidden = strip.scrollWidth <= strip.clientWidth + 2;
    }

    scheduleLayout() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.layout(); });
    }

    schedulePosition() {
      if (this.positionFrame) return;
      this.positionFrame = requestAnimationFrame(() => {
        this.positionFrame = null;
        this.updatePosition();
      });
    }

    updatePosition() {
      const placeholder = this.activePlaceholder();
      if (!placeholder?.isConnected) return;
      const anchor = placeholder.getBoundingClientRect();
      const panelHeight = this.panel.getBoundingClientRect().height;
      const docked = anchor.top <= window.innerHeight - panelHeight;
      // The UI remains outside transformed section ancestors. Absolute positioning
      // follows the original spacer once it meets the floating panel's top edge.
      this.style.setProperty('--selection-dock-top', `${anchor.top - this.getBoundingClientRect().top}px`);
      this.classList.toggle('is-docked', docked);
    }

    layout() {
      const markers = [...this.querySelectorAll('.campaign-selection__marker')];
      const laneEdges = [];
      let markerHeight = 36;
      markers.forEach((node) => {
        const rect = node.getBoundingClientRect();
        let lane = laneEdges.findIndex((edge) => rect.left >= edge + 8);
        if (lane < 0) lane = laneEdges.length;
        laneEdges[lane] = rect.right;
        node.querySelector('span').style.paddingTop = `${9 + lane * 24}px`;
        markerHeight = Math.max(markerHeight, node.getBoundingClientRect().height + 5);
      });
      const markerBox = this.querySelector('[data-selection-markers]');
      markerBox.style.height = `${markerHeight}px`;
      // Reserve room below the cards so the fixed panel does not cover the final row.
      const panelHeight = this.panel.getBoundingClientRect().height;
      this.placeholders.forEach((placeholder) => { placeholder.style.height = `${panelHeight}px`; });
      this.updatePosition();
      this.updateScroll();
    }
  }
  customElements.define('campaign-selection', CampaignSelection);
})();
